import { Directive, ElementRef, input, OnDestroy, inject, ViewContainerRef, TemplateRef, effect } from '@angular/core';
import { TooltipService, TooltipConfig, TooltipRef } from './tooltip.service';

/**
 * Shows a tooltip on hover and on keyboard focus. Escape or starting a drag
 * closes it, the pointer can move onto it without it closing, and a text tooltip is also
 * exposed to assistive technology as the element's description.
 *
 * A tooltip repeats or explains; it is never the only place essential
 * information appears.
 */
@Directive({
  selector: '[appTooltip]',
  host: {
    '(mouseenter)': 'onMouseEnter($event)',
    '(focusin)': 'onMouseEnter($event)',
    '(mouseleave)': 'onMouseLeave($event)',
    '(focusout)': 'onMouseLeave($event)',
    '(click)': 'onClick($event)'
  }
})
export class TooltipDirective implements OnDestroy {
  private elementRef = inject(ElementRef<HTMLElement>);
  private tooltipService = inject(TooltipService);
  private viewContainerRef = inject(ViewContainerRef);

  appTooltip = input<string | TemplateRef<unknown>>('');
  tooltipPosition = input<TooltipConfig['position']>('top');
  tooltipShowDelay = input<number>(100);
  tooltipHideDelay = input<number>(100);
  tooltipDisabled = input<boolean>(false);
  tooltipPanelClass = input<string | string[]>('');
  tooltipMaxWidth = input<string>('200px');

  private tooltipRef: TooltipRef | null = null;
  private showTimeoutId: number | null = null;
  private hideTimeoutId: number | null = null;
  /** The description this directive added, so it only ever removes its own. */
  private describedWith: string | null = null;

  private readonly onKeydown = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape' || !this.tooltipRef) return;
    // Closes the tooltip only; a dialog behind it stays open.
    event.stopPropagation();
    this.clearTimeouts();
    this.hideTooltip();
  };

  // A drag fires no mouseleave, so the tooltip would otherwise stay over the rows.
  private readonly onDragStart = (): void => {
    this.clearTimeouts();
    this.hideTooltip();
  };

  private readonly onPanelEnter = (): void => this.clearTimeouts();
  private readonly onPanelLeave = (): void => this.onMouseLeave();

  constructor() {
    // A text tooltip doubles as the element's accessible description.
    effect(() => {
      const content = this.appTooltip();
      const text = typeof content === 'string' && !this.tooltipDisabled() ? content.trim() : '';
      this.syncDescription(text);
    });
  }

  onMouseEnter(event?: Event): void {
    if (this.tooltipDisabled() || !this.appTooltip()) {
      return;
    }

    this.clearTimeouts();
    document.addEventListener('dragstart', this.onDragStart, true);

    this.showTimeoutId = window.setTimeout(() => {
      this.showTooltip();
    }, this.tooltipShowDelay());
  }

  onMouseLeave(event?: Event): void {
    this.clearTimeouts();

    this.hideTimeoutId = window.setTimeout(() => {
      this.hideTooltip();
    }, this.tooltipHideDelay());
  }

  onClick(event: Event): void {
    // Hide tooltip on click to prevent it from staying open
    this.clearTimeouts();
    this.hideTooltip();
  }

  ngOnDestroy(): void {
    this.clearTimeouts();
    this.hideTooltip();
    this.syncDescription('');
  }

  private showTooltip(): void {
    if (this.tooltipRef?.isOpen()) {
      return;
    }

    const config: TooltipConfig = {
      content: this.appTooltip(),
      position: this.tooltipPosition(),
      panelClass: this.tooltipPanelClass(),
      maxWidth: this.tooltipMaxWidth(),
      closeOnClickOutside: true
    };

    this.tooltipRef = this.tooltipService.show(this.elementRef.nativeElement, config, this.viewContainerRef);
    const panel = this.tooltipRef.element?.() ?? null;
    panel?.addEventListener('mouseenter', this.onPanelEnter);
    panel?.addEventListener('mouseleave', this.onPanelLeave);
    document.addEventListener('keydown', this.onKeydown, true);
  }

  private hideTooltip(): void {
    if (this.tooltipRef) {
      const panel = this.tooltipRef.element?.() ?? null;
      panel?.removeEventListener('mouseenter', this.onPanelEnter);
      panel?.removeEventListener('mouseleave', this.onPanelLeave);
      this.tooltipRef.close();
      this.tooltipRef = null;
    }
    document.removeEventListener('keydown', this.onKeydown, true);
    document.removeEventListener('dragstart', this.onDragStart, true);
  }

  private clearTimeouts(): void {
    if (this.showTimeoutId !== null) {
      clearTimeout(this.showTimeoutId);
      this.showTimeoutId = null;
    }

    if (this.hideTimeoutId !== null) {
      clearTimeout(this.hideTimeoutId);
      this.hideTimeoutId = null;
    }
  }

  /**
   * Sets the text as `aria-description` unless the element already says it
   * (its own text or name) or carries a description of its own.
   */
  private syncDescription(text: string): void {
    const host = this.elementRef.nativeElement as HTMLElement;
    const current = host.getAttribute('aria-description');
    if (this.describedWith !== null && current === this.describedWith) {
      host.removeAttribute('aria-description');
    }
    this.describedWith = null;
    if (!text || host.hasAttribute('aria-description')) return;
    const said = `${host.getAttribute('aria-label') ?? ''} ${visibleText(host)}`.replace(/\s+/g, ' ').trim();
    if (said.toLowerCase().includes(text.toLowerCase())) return;
    host.setAttribute('aria-description', text);
    this.describedWith = text;
  }
}

/** The element's own text, including what a shadow root shows (style text excluded). */
function visibleText(host: HTMLElement): string {
  const parts = [host.textContent ?? ''];
  const root = host.shadowRoot;
  if (root) {
    root.childNodes.forEach((node) => {
      if (node.nodeName !== 'STYLE') parts.push(node.textContent ?? '');
    });
  }
  return parts.join(' ');
}
